import Cookies from 'js-cookie';
import getRootDomain from 'utils/getRootDomain';

export type TiktokIntegration = {
  ttp: string | null;
  ttclid: string | null;
};

export type GoogleIntegration = {
  gclAw: string | null;
  gclGb: string | null;
  gclid: string | null;
  gbraid: string | null;
  wbraid: string | null;
};

const CLICK_IDS_SESSION_STORAGE_KEY = 'brij_click_ids';
const CLICK_ID_PARAMS = ['ttclid', 'gclid', 'gbraid', 'wbraid'];

// Click IDs are scoped to the experience that captured them: on brij.it
// hosting every brand shares the origin (and therefore sessionStorage), so an
// unscoped store would leak brand A's click IDs onto brand B's events when
// the same tab is reused across experiences.
const getExperienceSlug = (): string | null => {
  try {
    const segments = window.location.pathname.split('/').filter(Boolean);
    if (['c', 'p', 's'].includes(segments[0]) && segments[1]) {
      return segments[1];
    }
    return null;
  } catch (error) {
    return null;
  }
};

type StoredClickIds = { slug: string | null; ids: Record<string, string> };

const readClickIdStore = (): StoredClickIds | null => {
  try {
    const stored = sessionStorage.getItem(CLICK_IDS_SESSION_STORAGE_KEY);
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    if (
      parsed &&
      typeof parsed === 'object' &&
      !Array.isArray(parsed) &&
      parsed.ids &&
      typeof parsed.ids === 'object' &&
      !Array.isArray(parsed.ids)
    ) {
      return parsed as StoredClickIds;
    }
    return null;
  } catch (error) {
    return null;
  }
};

const getStoredClickIds = (): Record<string, string> => {
  const store = readClickIdStore();
  if (!store || store.slug !== getExperienceSlug()) return {};
  return store.ids;
};

// Click IDs arrive on the landing URL but the events that need them fire
// later in the flow, so they are persisted to sessionStorage at boot, keyed
// to the capturing experience slug. A URL value always wins over a stored
// one; params absent from the URL never erase stored values for the same
// experience; a different experience starts a fresh set. Errors are
// swallowed - this must never break boot.
export const captureClickIdsFromUrl = (): void => {
  try {
    const params = new URLSearchParams(window.location.search);
    const slug = getExperienceSlug();
    const store = readClickIdStore();
    const clickIds = store && store.slug === slug ? store.ids : {};
    let hasNewValue = false;
    CLICK_ID_PARAMS.forEach((param) => {
      const value = params.get(param);
      if (value) {
        clickIds[param] = value;
        hasNewValue = true;
      }
    });
    if (hasNewValue) {
      sessionStorage.setItem(
        CLICK_IDS_SESSION_STORAGE_KEY,
        JSON.stringify({ slug, ids: clickIds })
      );
    }
  } catch (error) {
    // Click-id capture is best-effort only.
  }
};

// Live URL param first (fresh read of location.search), then the
// boot-captured sessionStorage value for the current experience. Only string
// values are honored - tampered non-string JSON is ignored.
const getClickId = (param: string): string | null => {
  const liveValue = new URLSearchParams(window.location.search).get(param);
  if (liveValue) return liveValue;
  const storedValue = getStoredClickIds()[param];
  return typeof storedValue === 'string' && storedValue ? storedValue : null;
};

// _ttp is set by the brand's TikTok pixel on the brand's own domain; on
// brij.it-hosted pages it is deliberately nulled (same guard as
// getMetaIntegration). The guard applies only to cookie-sourced pixel values -
// click IDs (ttclid) are journey-scoped, not domain-scoped.
// Returns undefined in admin-registration/preview modes (the admin's own
// browser identifiers must never be stamped on a customer's events) and on
// any unexpected error (never block event logging).
export const getTiktokIntegration = (): TiktokIntegration | undefined => {
  try {
    const mode = (window as any).mode;
    if (mode === 'adminRegistration' || mode === 'preview') return undefined;

    const isBrijHosted = getRootDomain(window.location.href)?.includes(
      'brij.it'
    );
    return {
      ttp: isBrijHosted ? null : (Cookies.get('_ttp') as string) || null,
      ttclid: getClickId('ttclid') || (Cookies.get('ttclid') as string) || null,
    };
  } catch (error) {
    return undefined;
  }
};

// _gcl_aw/_gcl_gb are set by the brand's Google tag on the brand's own
// domain; same brij.it guard as above. gclid/gbraid/wbraid are click-ID
// params and are never guarded. Same undefined semantics as
// getTiktokIntegration.
export const getGoogleIntegration = (): GoogleIntegration | undefined => {
  try {
    const mode = (window as any).mode;
    if (mode === 'adminRegistration' || mode === 'preview') return undefined;

    const isBrijHosted = getRootDomain(window.location.href)?.includes(
      'brij.it'
    );
    return {
      gclAw: isBrijHosted ? null : (Cookies.get('_gcl_aw') as string) || null,
      gclGb: isBrijHosted ? null : (Cookies.get('_gcl_gb') as string) || null,
      gclid: getClickId('gclid'),
      gbraid: getClickId('gbraid'),
      wbraid: getClickId('wbraid'),
    };
  } catch (error) {
    return undefined;
  }
};
